import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';

import {
    Col,
    Row,
    Center,
} from '@zillow/zrs-comet';
import { Flex, Spacer } from '@zillow/constellation';

import { AddPropertyModal } from 'app/shared/components/Modal';
import { getTagTypeByKey, STATUS_KEY } from 'app/shared/utils/statusTagUtils';
import {
    AppSelectors,
    ModalSelectors,
    RoutingSelectors,
} from 'app/shared/selectors';
import ConnectedModuleCard from 'app/shared/components/ConnectedModuleCard';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import AddressDetails from 'app/shared/components/ConnectedPropertyCard/components/AddressDetails';
import ManageFindTenantsToggle from 'app/shared/components/ConnectedPropertyCard/components/ManageFindTenantsToggle';

import {
    StyledContainer,
    StyledMobileStatusSpacer,
    StyledPropertyPhotoContainer,
    StyledMobileStatusTag,
    StyledScrollContainer,
} from './StyledComponents';

const { PROPERTY_DETAILS_OVERVIEW } = STATUS_KEY;
const { PROPERTY_TYPES_BY_CODE } = PropertyConstants;

const propTypes = {
    match: PropTypes.object.isRequired,
    onFetchComplete: PropTypes.func,
    onFetchError: PropTypes.func,
};

const defaultProps = {
    onFetchComplete: () => {},
    onFetchError: () => {},
};

const ConnectedPropertyCard = ({
    match,
    onFetchComplete,
    onFetchError,
}) => {
    const dispatch = useDispatch();

    const addPropertyModal = useSelector(state => ModalSelectors.makeAddPropertyModal(state));
    const isMobile = useSelector(state => AppSelectors.makeIsScreenMobile(state));
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));
    const location = useSelector(state => RoutingSelectors.makeLocation(state));

    const [currentSection, setCurrentSection] = useState(location.pathname.split('/')[4]);
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isScrolledMobile, setIsScrolledMobile] = useState(false);

    const {
        isArchived,
        listingAlias,
        listingType,
        address,
        photoUrl,
        propertyType,
        status,
        isAddressEditable,
    } = useSelector(state => state.properties.propertyOverview);

    const handleScreenHeight = () => {
        if (window.pageYOffset >= 250 && isMobile) {
            setIsScrolledMobile(true);
        } else {
            setIsScrolledMobile(false);
        }
    };

    useEffect(() => {
        const getPropertyOverview = async () => {
            try {
                await dispatch(PropertiesActions.getOverview(listingId));
            } catch (err) {
                onFetchError();
                setHasError(true);
            } finally {
                setIsLoading(false);
                onFetchComplete();
            }
        };

        getPropertyOverview();
    }, [listingId]);

    useEffect(() => {
        dispatch(PropertiesActions.updateCurrentProperty({
            ...address,
            isArchived,
            isAddressEditable,
            listingAlias,
            listingTypeCode: listingType.code,
            propertyType: PROPERTY_TYPES_BY_CODE[propertyType.code],
        }));
    }, [listingAlias]);

    useEffect(() => {
        window.removeEventListener('scroll', handleScreenHeight);
        window.addEventListener('scroll', throttle(() => handleScreenHeight(isMobile), 200));

        return () => { window.removeEventListener('scroll', handleScreenHeight); };
    }, [isMobile]);

    return (
        <Row
            mediaQuery={{
                xs: { paddingBottom: 'sm' },
                xl: { paddingBottom: 'md' },
            }}
        >
            <Col>
                <ConnectedModuleCard
                    isLoading={isLoading}
                    spinnerHeight="150px"
                >
                    <StyledContainer>
                        {hasError ? (
                            <Flex display="flex" justifyContent="center" flexGrow="1">
                                <Spacer marginY="xl" padding="xl">
                                    <Center>There was an error loading your property. Please try again.</Center>
                                </Spacer>
                            </Flex>
                        ) : (
                            <>
                                <AddPropertyModal
                                    isOpen={addPropertyModal.isOpen}
                                    behavior={addPropertyModal.behavior}
                                />
                                <StyledPropertyPhotoContainer primaryPhotoUrl={photoUrl}>
                                    <StyledMobileStatusSpacer paddingLeft="xs" paddingTop="xs">
                                        {status && (
                                            <StyledMobileStatusTag
                                                appearance={getTagTypeByKey(PROPERTY_DETAILS_OVERVIEW, status)}
                                                size="sm"
                                                status={status}
                                            >
                                                {status}
                                            </StyledMobileStatusTag>
                                        )}
                                    </StyledMobileStatusSpacer>
                                </StyledPropertyPhotoContainer>
                                <StyledScrollContainer isScrolledMobile={isScrolledMobile}>
                                    <AddressDetails
                                        currentSection={currentSection}
                                        isScrolledMobile={isScrolledMobile}
                                    />

                                    <ManageFindTenantsToggle
                                        match={match}
                                        currentSection={currentSection}
                                        setCurrentSection={setCurrentSection}
                                    />

                                </StyledScrollContainer>
                            </>
                        )}
                    </StyledContainer>
                </ConnectedModuleCard>
            </Col>
        </Row>
    );
};

ConnectedPropertyCard.propTypes = propTypes;
ConnectedPropertyCard.defaultProps = defaultProps;

export default ConnectedPropertyCard;
